import { createApp } from 'vue'
import App from './App.vue'
import VueHighlightJS from 'vue3-highlightjs' // https://www.npmjs.com/package/vue3-highlightjs
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import router from "./router";

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
// Material Design Icons
import '@mdi/font/css/materialdesignicons.css'

// Import both light and dark highlight.js themes
import 'highlight.js/styles/github.css' // Light theme

// FSLabs global styles (Outfit font, noise texture, dark theme refinements)
import './assets/css/dark-mode.css'

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

// Initialize pinia first so we can use it right away
const app = createApp(App)
app.use(pinia)

// Import the store (must be after pinia initialization)
import { useStore } from './store/store'
const store = useStore()

// FSLabs Color Palette
// A cohesive color system for both light and dark themes
const fslabsLightTheme = {
  colors: {
    background: '#F0F0F2',
    surface: '#FFFFFF',
    'surface-variant': '#EEEEF0',
    'surface-bright': '#FFFFFF',
    primary: '#5a5a60',
    'primary-darken-1': '#44444a',
    'primary-lighten-1': '#76767c',
    secondary: '#7C4DFF',
    'secondary-darken-1': '#651FFF',
    'secondary-lighten-1': '#B388FF',
    'on-background': '#1A1A1E',
    'on-surface': '#1A1A1E',
    'on-surface-variant': '#5A5A60',
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    error: '#D32F2F',
    'on-error': '#FFFFFF',
    success: '#388E3C',
    'on-success': '#FFFFFF',
    warning: '#F57C00',
    'on-warning': '#FFFFFF',
    info: '#7C4DFF',
    'on-info': '#FFFFFF',
    outline: '#D0D0D4',
    'outline-variant': '#E4E4E8',
    'card-blue': '#EEEEF2',
    'card-purple': '#EEEEF2',
    'card-teal': '#EEEEF2',
  }
}

const fslabsDarkTheme = {
  colors: {
    background: '#000000',
    surface: '#1e1e22',
    'surface-variant': '#2a2a2e',
    'surface-bright': '#333338',
    primary: '#b0b0b4',           // monochrome gray - buttons, active elements
    'primary-darken-1': '#909096',
    'primary-lighten-1': '#d0d0d4',
    secondary: '#7C4DFF',         // purple accent
    'secondary-darken-1': '#651FFF',
    'secondary-lighten-1': '#B388FF',
    'on-background': '#e8e8ea',
    'on-surface': '#e8e8ea',
    'on-surface-variant': '#909096',
    'on-primary': '#111113',
    'on-secondary': '#FFFFFF',
    error: '#EF5350',
    'on-error': '#111113',
    success: '#66BB6A',
    'on-success': '#111113',
    warning: '#FFA726',
    'on-warning': '#111113',
    info: '#7C4DFF',
    'on-info': '#FFFFFF',
    outline: '#3a3a3e',
    'outline-variant': '#2a2a2e',
    'card-blue': '#2a2a30',
    'card-purple': '#2a2a30',
    'card-teal': '#2a2a30',
  }
}

// Create Vuetify instance with theme from store
const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: store.theme,
    themes: {
      light: fslabsLightTheme,
      dark: fslabsDarkTheme
    }
  },
  icons: {
    defaultSet: 'mdi' // Use Material Design Icons as default
  }
})

// Complete the app initialization
app
  .use(router)
  .use(VueHighlightJS)
  .use(vuetify)
  .mount('#app')
